import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout';
import Body from '../../components/body';
import Heading from '../../components/heading';
import Seo from '../../components/seo';
import { scaleLinear, scaleBand } from 'd3-scale';
import { max } from 'd3-array';
import Code from '../../components/code';

const fruits = [
    { name: "🍊", count: 21 },
    { name: "🍇", count: 13 },
    { name: "🍏", count: 8 },
    { name: "🍌", count: 5 },
    { name: "🍐", count: 3 },
    { name: "🍋", count: 2 },
    { name: "🍎", count: 1 },
    { name: "🍉", count: 1 }
]


const HorizontalBar = () => {
    const margin = ({ top: 40, bottom: 20, left: 40, right: 20 })
    const [width, setWidth] = useState(400)
    const height = 500;
    const xScale = scaleLinear()
        .domain([0, max(fruits, d => d.count)])
        .range([0, width - 3 * margin.right - margin.left - 20])
    const yScale = scaleBand()
        .domain(fruits.map(d => d.name))
        .range([margin.top, height - margin.bottom])
        .padding(0.1)
    const updateDimensions = () => {
        setWidth(window.outerWidth);
    }
    useEffect(() => {
        setWidth(window.outerWidth);
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);
    return (
        <Layout>
            <Seo title="HorizontalBar Charts" />
            <Heading heading="HorizontalBar Chart" />
            <Body>
                <p >
                    The Horizontal Bar chart is the most simple form of chart that can be created with D3. Svg coordinates starts
                    from top left corner of the screen and move towards the right so it's easier to create an element that starts from left and grows
                    towards right. This makes it easy to create a horizontal chart with left base and growing towards right.
                    At first, the data will be converted to pixel representation with D3 and then react and svg will be used for projection
                    it on screen. I am using D3 with React. The data set used here is
                </p>
                <Code>
                    {`const fruits=
    [
        { name: "🍊", count: 21 },
        { name: "🍇", count: 13 },
        { name: "🍏", count: 8 },
        { name: "🍌", count: 5 },
        { name: "🍐", count: 3 },
        { name: "🍋", count: 2 },
        { name: "🍎", count: 1 },
        { name: "🍉", count: 1 }
    ] `}
                </Code>
                <p>
                    The count value will be projected on the horizontal x-scale with the help of linear scale from D3. The vertical y-axis
                    will represent an individual item and will be using a band scale.
                </p>
                <Code>
                    {`xScale=scaleLinear()
    .domain([lowestValue,maximumValue])
    .range([pixelStart,pixelEnd])
    
yScale=scaleBand()
    .domain(allElements)
    .range([pixelStart,pixelEnd])
    .padding(space between bar items)`}
                </Code>
                <p>
                    The data will be used to create rect svg elements which will represent an individual
                    bar.
                </p>
                <Code>
                    {`<rect 
    x={xStartPixel} 
    y={yStartPixel}/>
    width={how hight one bar will be}
    height={how fat the bar will be}`}
                </Code>
                <p>
                    So the code is
                </p>
                <Code>
                    {`
const xScale = scaleLinear()
    .domain([0, max(fruits, d => d.count)])
    .range([margin.left, width - 3 * margin.right - margin.left])

const yScale = scaleBand()
    .domain(fruits.map(d => d.name))
    .range([margin.top, height - margin.bottom])
    .padding(0.1)
    
<rect 
    key={index} 
    x={0} 
    y={yScale(a.name)} 
    width={xScale(a.count)} 
    height={yScale.bandwidth()}>`}
                </Code>
                <p>
                    The data <b>fruits</b> is looped through and the value <b>a</b> is provided to each
                    rect
                </p>
                <div className="my-2 sm:my-4 border dark:bg-gray-500 bg-gray-200 rounded">
                    <svg
                        width={width - 50}
                        height={height}
                        className="fill-current text-gray-700 dark:text-white">
                        {
                            fruits.map((a, index) => (
                                <g key={`group-${index}`} transform={`translate(${margin.left},0)`}>
                                    <rect key={index} x={0} y={yScale(a.name)} width={xScale(a.count)} height={yScale.bandwidth()}>
                                        <title key={`title-${index}`}>
                                            {a.count}
                                        </title>
                                    </rect>
                                    <text key={`text-${index}`} textAnchor="middle" dx="0.7em" className="text-white dark:text-gray-700" x={xScale(a.count) - 20} y={yScale(a.name) + yScale.bandwidth() / 2}>{a.count}</text>
                                </g>
                            ))
                        }
                    </svg>
                </div>
                <p>
                    Next process is to add axis to indicate values on the axis.
                    We will be using ticks property of linear scale as it will gives us an equal descending interval
                    and we will use domain from band scale since the data is discontinous.
                </p>
                <div className="my-2 sm:my-4 border dark:bg-gray-500 bg-gray-200 rounded">
                    <svg
                        width={width - 50}
                        height={height}
                        className="fill-current text-gray-700 dark:text-white">

                        {xScale.ticks().map((a, index) => (
                            <g transform={`translate(${margin.left},0)`} key={`gTicks-${index}`} className="stroke-current text-gray-400 dark:text-gray-50">
                                <line
                                    key={index}
                                    x1={xScale(a)}
                                    x2={xScale(a)}
                                    y1={margin.top + 5}
                                    y2={margin.top * 0.9}
                                />
                                <text
                                    key={`xScaleText-$index}`}
                                    textAnchor="middle"
                                    x={xScale(a)}
                                    y={(margin.top * 0.75)}
                                    className="text-gray-700 dark:text-white text-xs">
                                    {a}
                                </text>
                            </g>
                        ))}
                        {
                            yScale.domain().map((a, index) => (
                                <g key={`gTicks-${index}`} className="stroke-current text-gray-400 dark:text-gray-50">
                                    <line
                                        key={`yScale-${index}`}
                                        x1={margin.left + 5}
                                        x2={margin.left * 0.9}
                                        y1={yScale(a) + yScale.bandwidth() / 2}
                                        y2={yScale(a) + yScale.bandwidth() / 2}
                                    />
                                    <text
                                        key={`yScaleText-${index}`}
                                        textAnchor="end"
                                        dy="0.3em"
                                        x={margin.left * 0.75}
                                        y={yScale(a) + yScale.bandwidth() / 2}
                                        className="text-gray-700 dark:text-white text-xs">
                                        {a}
                                    </text>
                                </g>
                            ))
                        }
                        {
                            fruits.map((a, index) => (
                                <g key={`group-${index}`} transform={`translate(0,0)`}>
                                    <rect
                                        key={index}
                                        x={margin.left}
                                        y={yScale(a.name)}
                                        width={xScale(a.count)}
                                        height={yScale.bandwidth()}>
                                        <title key={`title-${index}`}>
                                            {a.count}
                                        </title>
                                    </rect>
                                    <text key={`textAll-${index}`} textAnchor="middle" dx="0.8em" className="text-xs text-white dark:text-gray-700" x={xScale(a.count) + 20} y={yScale(a.name) + yScale.bandwidth() / 2}>{a.count}</text>
                                </g>
                            ))
                        }
                    </svg>
                </div>
                <p>
                    To add the axis we have used the line component and the text component from the svg.
                </p>
                <Code>
                    {`
<line
    key={index}
    x1={xScale(a)}
    x2={xScale(a)}
    y1={margin.top + 5}
    y2={(margin.top * 0.95)}
/>
<text
    key={'xScaleText-{index}'}
    textAnchor="middle"
    x={xScale(a)}
    y={(margin.top * 0.75)}
    >
    {a}
</text>

<line
    key='yScale-{index}'
    x1={margin.left + 5}
    x2={margin.left * 0.9}
    y1={yScale(a) + yScale.bandwidth() / 2}
    y2={yScale(a) + yScale.bandwidth() / 2}
    />
<text
    key='yScaleText-$index'
    textAnchor="end"
    dy="0.3em"
    x={margin.left * 0.75}
    y={yScale(a) + yScale.bandwidth() / 2}
    {a}
</text>`}
                </Code>
                <p>
                    Due to the formatting of code in html the <b>Key tag are missing bracket in index</b>.
                    We have used line to create the tick and text to display the value. Some css
                    was added on top for display asthetics. The ticks are displayed with the help of margins.
                    The chart is moved inside by a fixed margin top and left where the ticks are placed.
                </p>
                <p>
                    Adding name to the axis to make it clear on what the axis data is.
                </p>
                <div className="my-2 sm:my-4 border dark:bg-gray-500 bg-gray-200 rounded">
                    <svg
                        width={width - 50}
                        height={height}
                        className="fill-current text-gray-700 dark:text-white">
                        <text x={(width - margin.left - margin.right) / 2} y={margin.top * 0.3} key="xScale" className="xs:text-xs text-base font-bold">Fruit Counts →</text>
                        <text
                            key="yScale"
                            className="xs:text-xs text-base font-bold"
                            transform={`translate(${margin.left * 0.3},${(height - margin.bottom - margin.top) / 2}) rotate(-90)`}>Fruits</text>
                        {xScale.ticks().map((a, index) => (
                            <g transform={`translate(${margin.left},0)`} key={`gTicks-${index}`} className="stroke-current text-gray-400 dark:text-gray-50">
                                <line
                                    key={index}
                                    x1={xScale(a)}
                                    x2={xScale(a)}
                                    y1={margin.top + 5}
                                    y2={margin.top * 0.9}
                                />
                                <text
                                    key={`xScaleText-$index}`}
                                    textAnchor="middle"
                                    x={xScale(a)}
                                    y={(margin.top * 0.75)}
                                    className="text-gray-700 dark:text-white text-xs">
                                    {a}
                                </text>
                            </g>
                        ))}
                        {
                            yScale.domain().map((a, index) => (
                                <g key={`gTicks-${index}`} className="stroke-current text-gray-400 dark:text-gray-50">
                                    <line
                                        key={`yScale-${index}`}
                                        x1={margin.left + 5}
                                        x2={margin.left * 0.9}
                                        y1={yScale(a) + yScale.bandwidth() / 2}
                                        y2={yScale(a) + yScale.bandwidth() / 2}
                                    />
                                    <text
                                        key={`yScaleText-${index}`}
                                        textAnchor="end"
                                        dy="0.3em"
                                        x={margin.left * 0.75}
                                        y={yScale(a) + yScale.bandwidth() / 2}
                                        className="text-gray-700 dark:text-white text-xs">
                                        {a}
                                    </text>
                                </g>
                            ))
                        }
                        {
                            fruits.map((a, index) => (
                                <g key={`group-${index}`} transform={`translate(0,0)`}>
                                    <rect
                                        key={index}
                                        x={margin.left}
                                        y={yScale(a.name)}
                                        width={xScale(a.count)}
                                        height={yScale.bandwidth()}>
                                        <title key={`title-${index}`}>
                                            {a.count}
                                        </title>
                                    </rect>
                                    <text key={`text2All-${index}`} textAnchor="middle" dx="0.8em" className="text-xs text-white dark:text-gray-700" x={xScale(a.count) + 20} y={yScale(a.name) + yScale.bandwidth() / 2}>{a.count}</text>
                                </g>
                            ))
                        }
                    </svg>
                </div>
                <p>
                    To do this I added two text item with the index name and placed them in the spacing position with svg text.
                </p>
            </Body>
        </Layout>
    )
}

export default HorizontalBar